import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { XADropdownConfig } from 'src/app/model/xa-dropdown-model';
import { CommonService } from 'src/app/services/common/common.service';
import { OnboardingService } from 'src/app/services/onboarding/onboarding.service';
import { environment } from 'src/environments/environment';

type SignupStep = 'form' | 'emailOtp' | 'mobileOtp' | 'success';

@Component({
    selector: 'app-free-trial-signup',
    templateUrl: './free-trial-signup.component.html',
    styleUrls: ['./free-trial-signup.component.scss'],
    standalone: false
})
export class FreeTrialSignupComponent implements OnInit, OnDestroy {
  currentStep: SignupStep = 'form';
  hideNotification = false;
  signupForm: FormGroup;
  emailOtpValue = '';
  mobileOtpValue = '';
  emailOtpStatus: 'default' | 'error' | 'success' = 'default';
  mobileOtpStatus: 'default' | 'error' | 'success' = 'default';
  emailOtpError = '';
  mobileOtpError = '';
  emailResendCounter = 30;
  mobileResendCounter = 30;
  isSubmitting = false;
  isLoadingLookups = false;
  countryList: Array<any> = [];
  countryConfig: XADropdownConfig = {
    name: 'country',
    label: 'country',
    labelKey: 'name',
    valueKey: 'id',
    showRequiredLabel: true,
    enableSearch: true
  };
  codeConfig: XADropdownConfig = {
    name: 'mobileCountryCode',
    labelKey: 'phoneCountryCode',
    valueKey: 'phoneCountryCode',
    showHeaderIcon: true,
    showRequiredLabel: true,
    enableSearch: true
  };
  otpConfig = {
    length: 4,
    allowNumbersOnly: true,
    disableAutoFocus: false,
    inputClass: 'free-trial-otp-input'
  };
  readonly planType = 'FREE_TRIAL';
  readonly disallowedEmailDomains: string[] = [];
  readonly disableRecaptchaForSelfSignup = (environment as { disableRecaptchaForSelfSignup?: boolean }).disableRecaptchaForSelfSignup ?? false;
  private readonly recaptchaLoadTimeoutMs = 10000;
  private emailResendTimerId: ReturnType<typeof setInterval> | null = null;
  private mobileResendTimerId: ReturnType<typeof setInterval> | null = null;
  private recaptchaLoadTimerId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Constructor
   * @param formBuilder - FormBuilder
   * @param onboardingService - OnboardingService
   * @param commonService - CommonService
   */
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly onboardingService: OnboardingService,
    public commonService: CommonService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  /**
   * On init
   */
  ngOnInit(): void {
    this.commonService.hideLoading();
    this.initializeForm();
    if (this.disableRecaptchaForSelfSignup) {
      this.setRecaptchaRequired(false);
    } else {
      this.startRecaptchaLoadFallbackTimer();
    }
    this.watchCountryChanges();
    this.watchEmailChanges();
    this.bootstrapGuestTokenAndLoadLookups();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.clearTimers();
    this.clearRecaptchaLoadTimer();
    this.commonService.hideLoading();
  }

  /**
   * Get form controls
   * @returns { [key: string]: AbstractControl }
   */
  get formControls(): { [key: string]: AbstractControl } {
    return this.signupForm.controls;
  }

  /**
   * Get is form step
   * @returns { boolean }
   */
  get isFormStep(): boolean {
    return this.currentStep === 'form';
  }

  /**
   * Get is email otp step
   * @returns { boolean }
   */
  get isEmailOtpStep(): boolean {
    return this.currentStep === 'emailOtp';
  }

  /**
   * Get is mobile otp step
   * @returns { boolean }
   */
  get isMobileOtpStep(): boolean {
    return this.currentStep === 'mobileOtp';
  }

  /**
   * Get is success step
   * @returns { boolean }
   */
  get isSuccessStep(): boolean {
    return this.currentStep === 'success';
  }

  /**
   * Get email address
   * @returns { string }
   */
  get emailAddress(): string {
    return this.signupForm.get('email')?.value || '';
  }

  /**
   * Get mobile number
   * @returns { string }
   */
  get mobileNumber(): string {
    const code = this.signupForm.get('mobileCountryCode')?.value || '';
    const phone = this.signupForm.get('mobileNumber')?.value || '';
    return [ code, phone ].filter(Boolean).join(' ').trim();
  }

  /**
   * Get disable form submit
   * @returns { boolean }
   */
  get disableFormSubmit(): boolean {
    return this.signupForm.invalid || this.isSubmitting || this.isLoadingLookups;
  }

  /**
   * Get disable email otp submit
   * @returns { boolean }
   */
  get disableEmailOtpSubmit(): boolean {
    return this.emailOtpValue.length !== 4 || this.isSubmitting;
  }

  /**
   * Get disable mobile otp submit
   * @returns { boolean }
   */
  get disableMobileOtpSubmit(): boolean {
    return this.mobileOtpValue.length !== 4 || this.isSubmitting;
  }

  /**
   * Submit signup
   */
  submitSignup(): void {
    if (this.disableFormSubmit) {
      this.signupForm.markAllAsTouched();
      return;
    }

    const payload = this.buildSignupPayload();
    this.isSubmitting = true;
    this.commonService.showLoading();
    this.submitSignupForm(payload)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.commonService.hideLoading();
        })
      )
      .subscribe((result) => {
        if (result) {
          this.currentStep = 'emailOtp';
          this.resetEmailOtpState();
          this.startEmailResendCountdown();
        }
      });
  }

  /**
   * Verify email otp step
   */
  verifyEmailOtpStep(): void {
    if (this.disableEmailOtpSubmit) {
      return;
    }

    this.isSubmitting = true;
    this.commonService.showLoading();
    this.verifyEmailOtp(this.emailOtpValue)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.commonService.hideLoading();
        })
      )
      .subscribe((result) => {
        if (result?.verificationResult?.isValid) {
          this.emailOtpStatus = 'success';
          this.emailOtpError = '';

          //THIS STEP SHOULD BE REMOVED AFTER THE MOBILE OTP VERIFICATION IS FIXED IN THE BACKEND
          this.mobileOtpValue = '1234';
          this.isSubmitting = true;
          this.commonService.showLoading();
          this.verifyMobileOtp(this.mobileOtpValue)
            .pipe(finalize(() => {
              this.isSubmitting = false;
              this.commonService.hideLoading();
            }))
            .subscribe((result) => {
              if (result?.verificationResult == null || result?.verificationResult?.isValid) {
                this.mobileOtpStatus = 'success';
                this.mobileOtpError = '';
                this.currentStep = 'success';
                return;
              }
      
              this.mobileOtpStatus = 'error';
              this.mobileOtpError = this.getOtpError(result);
            });

          //THIS STEP SHOULD BE UNCOMMENTED AFTER THE MOBILE OTP VERIFICATION IS FIXED IN THE BACKEND
          // this.currentStep = 'mobileOtp';
          // this.resetMobileOtpState();
          // this.startMobileResendCountdown();
          return;
        }

        this.emailOtpStatus = 'error';
        this.emailOtpError = this.getOtpError(result);
      });
  }

  /**
   * Verify mobile otp step
   */
  verifyMobileOtpStep(): void {
    if (this.disableMobileOtpSubmit) {
      return;
    }

    this.isSubmitting = true;
    this.commonService.showLoading();
    this.verifyMobileOtp(this.mobileOtpValue)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.commonService.hideLoading();
        })
      )
      .subscribe((result) => {
        if (result?.verificationResult?.isValid) {
          this.mobileOtpStatus = 'success';
          this.mobileOtpError = '';
          this.currentStep = 'success';
          return;
        }

        this.mobileOtpStatus = 'error';
        this.mobileOtpError = this.getOtpError(result);
      });
  }

  /**
   * On email otp change
   * @param value - string
   */
  onEmailOtpChange(value: string): void {
    this.emailOtpValue = value;
    this.emailOtpStatus = 'default';
    this.emailOtpError = '';
  }

  /**
   * On mobile otp change
   * @param value - string
   */
  onMobileOtpChange(value: string): void {
    this.mobileOtpValue = value;
    this.mobileOtpStatus = 'default';
    this.mobileOtpError = '';
  }

  /**
   * Handle recaptcha error
   */
  onRecaptchaError(): void {
    this.clearRecaptchaLoadTimer();
    this.signupForm.get('recaptchaKey')?.setValue('');
    this.setRecaptchaRequired(false);
  }

  /**
   * Resend email code
   */
  resendEmailCode(): void {
    if (this.emailResendCounter > 0) {
      return;
    }

    this.resendEmailOtp().subscribe(() => {
      this.resetEmailOtpState();
      this.startEmailResendCountdown();
    });
  }

  /**
   * Resend mobile code
   */
  resendMobileCode(): void {
    if (this.mobileResendCounter > 0) {
      return;
    }

    this.resendMobileOtp().subscribe(() => {
      this.resetMobileOtpState();
      this.startMobileResendCountdown();
    });
  }

  /**
   * Get field error
   * @param controlName - string
   * @returns { string }
   */
  getFieldError(controlName: string): string {
    const control = this.signupForm.get(controlName);
    if (!control || !(control.touched || control.dirty) || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.translateService.instant('field_required');
    }

    if (control.errors['maxlength']) {
      return this.translateService.instant('free_trial_max_length_error', {
        value: control.errors['maxlength'].requiredLength
      });
    }

    if (control.errors['email']) {
      return this.translateService.instant('free_trial_valid_business_email');
    }

    if (control.errors['invalidEmailDomain']) {
      return this.translateService.instant('free_trial_valid_business_domain');
    }

    if (control.errors['disallowedEmailDomain']) {
      return this.translateService.instant('free_trial_disallowed_email_domain');
    }

    if (control.errors['pattern']) {
      return this.translateService.instant('free_trial_valid_value');
    }

    return this.translateService.instant('free_trial_valid_value');
  }

  /**
   * Initialize form
   */
  private initializeForm(): void {
    this.signupForm = this.formBuilder.group({
      businessName: [ '', [ Validators.required, Validators.maxLength(200) ] ],
      email: [ '', [ Validators.required, Validators.maxLength(254), Validators.email, this.businessEmailDomainValidator() ] ],
      mobileCountryCode: [ '', Validators.required ],
      mobileNumber: [ '', Validators.required ],
      country: [ '', Validators.required ],
      recaptchaKey: [ '', Validators.required ],
      planType: [ this.planType ]
    });
  }

  /**
   * Set recaptcha required validation
   * @param isRequired - boolean
   */
  private setRecaptchaRequired(isRequired: boolean): void {
    const control = this.signupForm.get('recaptchaKey');
    control?.setValidators(isRequired ? [ Validators.required ] : []);
    control?.updateValueAndValidity();
  }

  /**
   * Start recaptcha load fallback timer
   */
  private startRecaptchaLoadFallbackTimer(): void {
    this.clearRecaptchaLoadTimer();
    this.recaptchaLoadTimerId = setTimeout(() => {
      const hasRecaptchaIframe = !!document.querySelector('re-captcha iframe, iframe[src*="recaptcha"]');
      const hasRecaptchaFallbackIframe = !!document.querySelector(
        're-captcha iframe[src*="recaptcha/api/fallback"], iframe[src*="recaptcha/api/fallback"]'
      );
      const recaptchaValue = this.signupForm.get('recaptchaKey')?.value;
      if ((!hasRecaptchaIframe || hasRecaptchaFallbackIframe) && !recaptchaValue) {
        this.onRecaptchaError();
      }
    }, this.recaptchaLoadTimeoutMs);
  }

  /**
   * Clear recaptcha load timer
   */
  private clearRecaptchaLoadTimer(): void {
    if (this.recaptchaLoadTimerId) {
      clearTimeout(this.recaptchaLoadTimerId);
      this.recaptchaLoadTimerId = null;
    }
  }

  /**
   * Watch mobile country code changes for phone validation.
   */
  private watchCountryChanges(): void {
    this.signupForm.get('mobileCountryCode')?.valueChanges.subscribe((code) => {
      const selectedCode = this.countryList.find(item => item.phoneCountryCode === code);
      const control = this.signupForm.get('mobileNumber');
      control?.setValidators(
        [ Validators.required, selectedCode?.regExpression ? Validators.pattern(selectedCode.regExpression) : null ].filter(Boolean) as ValidatorFn[]
      );
      control?.updateValueAndValidity();
    });
  }

  /**
   * Watch email changes
   */
  private watchEmailChanges(): void {
    this.signupForm.get('email')?.valueChanges.subscribe((value: string) => {
      const normalizedValue = (value || '').trim().toLowerCase();
      if (normalizedValue && !normalizedValue.includes('@')) {
        return;
      }
    });
  }

  /**
   * Load country list
   */
  private loadCountryList(): void {
    this.isLoadingLookups = true;
    this.commonService.showLoading();
    this.onboardingService
      .getCountryList()
      .pipe(
        catchError(() => of([])),
        finalize(() => {
          this.isLoadingLookups = false;
          this.commonService.hideLoading();
        })
      )
      .subscribe((countries: any[]) => {
        this.countryList = Array.isArray(countries) ? countries : [];
      });
  }

  /**
   * Build signup payload
   * @returns { Record<string, any> }
   */
  private buildSignupPayload(): Record<string, any> {
    const email = this.emailAddress.trim().toLowerCase();
    const country = this.countryList.find(item => item.id === this.signupForm.get('country')?.value);

    return {
      businessName: this.signupForm.get('businessName')?.value?.trim(),
      phone: this.getSignupPhoneNumber(),
      email,
      country: country?.name || this.signupForm.get('country')?.value,
      selfRegistrationType: 2,
      isB2CRequired: true
    };
  }

  /**
   * Bootstrap guest token and load lookups
   */
  private bootstrapGuestTokenAndLoadLookups(): void {
    this.isLoadingLookups = true;
    this.commonService.showLoading();
    this.onboardingService
      .getToken()
      .pipe(
        switchMap((response: any) => {
          if (response?.access_token) {
            localStorage.setItem('externalToken', response.access_token);
            return this.onboardingService.getCountryODataList();
          }

          localStorage.removeItem('externalToken');
          return of([]);
        }),
        catchError(() => {
          localStorage.removeItem('externalToken');
          return of([]);
        }),
        finalize(() => {
          this.isLoadingLookups = false;
          this.commonService.hideLoading();
        })
      )
      .subscribe((countries: any[] | any) => {
        const list = countries.value?.map((item: any) => ({
          ...item,
          urlFlag: item.UrlFlag,
          name: item.Name,
          id: item.Id,
          phoneCountryCode: item.PhoneCountryCode,
          regExpression: item.RegExpression,
          mobileNumberFormat: item.MobileNumberFormat
        }));
        this.countryList = Array.isArray(list) ? list : [] ;
      });
  }

  /**
   * Build otp validation payload
   * @param code
   * @param isEmail
   * @returns
   */
  private buildOtpValidationPayload(code: string, isEmail: boolean): Record<string, any> {
    return {
      email: this.emailAddress.trim().toLowerCase(),
      phoneNumber: this.getValidationPhoneNumber(),
      code,
      isEmail,
      productName: 'Addenda Agentix'
    };
  }

  /**
   * Get signup phone number
   * @returns { string }
   */
  private getSignupPhoneNumber(): string {
    const countryCode = `${this.signupForm.get('mobileCountryCode')?.value || ''}`.trim();
    const mobileNumber = `${this.signupForm.get('mobileNumber')?.value || ''}`.trim();
    return `${countryCode} ${mobileNumber}`;
  }

  /**
   * Get validation phone number
   * @returns { string }
   */
  private getValidationPhoneNumber(): string {
    const countryCode = `${this.signupForm.get('mobileCountryCode')?.value || ''}`.trim();
    const mobileNumber = `${this.signupForm.get('mobileNumber')?.value || ''}`.trim();
    return `${countryCode}${mobileNumber}`;
  }

  /**
   * Get otp error
   * @param response
   * @returns { string }
   */
  private getOtpError(response: any): string {
    return response?.verificationResult?.errors?.[0] || this.translateService.instant('free_trial_invalid_otp_code');
  }

  /**
   * Business email domain validator
   * @returns { ValidatorFn }
   */
  private businessEmailDomainValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = (control.value || '').trim().toLowerCase();
      if (!rawValue) {
        return null;
      }

      const parts = rawValue.split('@');
      if (parts.length !== 2) {
        return null;
      }

      const domain = parts[1];
      if (!domain.includes('.') || this.isIpLiteral(domain)) {
        return { invalidEmailDomain: true };
      }

      if (this.disallowedEmailDomains.includes(domain)) {
        return { disallowedEmailDomain: true };
      }

      return null;
    };
  }

  /**
   * Is ip literal
   * @param domain - string
   * @returns { boolean }
   */
  private isIpLiteral(domain: string): boolean {
    const normalizedDomain = domain.replaceAll('[', '').replaceAll(']', '');
    return /^(\d{1,3}\.){3}\d{1,3}$/.test(normalizedDomain);
  }

  /**
   * Start email resend countdown
   */
  private startEmailResendCountdown(): void {
    if (this.emailResendTimerId) {
      clearInterval(this.emailResendTimerId);
    }

    this.emailResendCounter = 30;
    this.emailResendTimerId = setInterval(() => {
      if (this.emailResendCounter > 0) {
        this.emailResendCounter--;
        this.cdr.markForCheck();
        return;
      }

      if (this.emailResendTimerId) {
        clearInterval(this.emailResendTimerId);
        this.emailResendTimerId = null;
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  /**
   * Start mobile resend countdown
   */
  private startMobileResendCountdown(): void {
    if (this.mobileResendTimerId) {
      clearInterval(this.mobileResendTimerId);
    }

    this.mobileResendCounter = 30;
    this.mobileResendTimerId = setInterval(() => {
      if (this.mobileResendCounter > 0) {
        this.mobileResendCounter--;
        this.cdr.markForCheck();
        return;
      }

      if (this.mobileResendTimerId) {
        clearInterval(this.mobileResendTimerId);
        this.mobileResendTimerId = null;
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  /**
   * Reset email otp state
   */
  private resetEmailOtpState(): void {
    this.emailOtpValue = '';
    this.emailOtpStatus = 'default';
    this.emailOtpError = '';
  }

  /**
   * Reset mobile otp state
   */
  private resetMobileOtpState(): void {
    this.mobileOtpValue = '';
    this.mobileOtpStatus = 'default';
    this.mobileOtpError = '';
  }

  /**
   * Clear timers
   */
  private clearTimers(): void {
    if (this.emailResendTimerId) {
      clearInterval(this.emailResendTimerId);
      this.emailResendTimerId = null;
    }

    if (this.mobileResendTimerId) {
      clearInterval(this.mobileResendTimerId);
      this.mobileResendTimerId = null;
    }
  }

  /**
   * Submit signup form
   * @param formValue - Record<string, any>
   * @returns { Observable<Record<string, any>> }
   */
  private submitSignupForm(formValue: Record<string, any>): Observable<Record<string, any> | null> {
    formValue = { ...formValue, productName: 'Addenda Agentix' };
    return this.onboardingService.autoApprovalSelfRegistration(formValue).pipe(
      map((response: any) => (response?.success ? response : null)),
      catchError(() => of(null))
    );
  }

  /**
   * Verify email otp
   * @param code - string
   * @returns { Observable<boolean> }
   */
  private verifyEmailOtp(code: string): Observable<any> {
    return this.onboardingService.validateSelfRegistrationOtp(this.buildOtpValidationPayload(code, true)).pipe(catchError(() => of(null)));
  }

  /**
   * Verify mobile otp
   * @param code - string
   * @returns { Observable<boolean> }
   */
  private verifyMobileOtp(code: string): Observable<any> {
    return this.onboardingService.validateSelfRegistrationOtp(this.buildOtpValidationPayload(code, false)).pipe(catchError(() => of(null)));
  }

  /**
   * Resend email otp
   * @returns { Observable<boolean> }
   */
  private resendEmailOtp(): Observable<boolean> {
    return of(true);
  }

  /**
   * Resend mobile otp
   * @returns { Observable<boolean> }
   */
  private resendMobileOtp(): Observable<boolean> {
    return of(true);
  }

  /**
   * Go back
   */
  goBack(): void {
    if (this.isEmailOtpStep) {
      this.currentStep = 'form';
      return;
    }

    if (this.isMobileOtpStep) {
      this.currentStep = 'emailOtp';
      return;
    }

    //browser back
    window.history.back();
  }
}
